import {
    ButtonBase,
    Chip,
    Switch,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    type Theme,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import type { MercedesStatus, Vehicle } from '../types';

/** Connection chip from the local mercedesStatus column — no Mercedes call on the list. */
export function MercedesChip({ status }: { status: MercedesStatus }) {
    const { t } = useTranslation();
    if (status === 'NONE') {
        return <Chip label={t('vehicles.connection.NONE')} />;
    }
    if (status === 'CONNECTED') {
        return <Chip color="success" variant="outlined" label={t('vehicles.connection.CONNECTED')} />;
    }
    return <Chip color="warning" variant="outlined" label={t('vehicles.connection.BUSY')} />;
}

/** Status columns shrink to their content so display name and notes get the room. */
const SHRINK = { width: '1%', whiteSpace: 'nowrap' } as const;

/**
 * Every switch is disabled while a PUT runs (one at a time), but its grey only fades in once the
 * request outlasts 150 ms, so a fast toggle does not flash the whole column. The thumb colour joins
 * MUI's slide transition; the delay holds the disabled colours only, never the slide.
 */
const SWITCH_SX = {
    '& .MuiSwitch-switchBase': {
        transition: (theme: Theme) =>
            theme.transitions.create(['left', 'transform', 'color'], {
                duration: theme.transitions.duration.shortest,
            }),
    },
    '& .MuiSwitch-switchBase.Mui-disabled': { transitionDelay: '0ms, 0ms, 150ms' },
    '& .MuiSwitch-switchBase.Mui-disabled + .MuiSwitch-track': { transitionDelay: '150ms' },
};

export function VehicleTable({
    rows,
    canEdit,
    pending,
    onOpen,
    onToggleAvailability,
}: {
    rows: Vehicle[];
    canEdit: boolean;
    /** An availability request is in flight — every switch is disabled (one PUT at a time). */
    pending: boolean;
    onOpen: (vehicle: Vehicle) => void;
    onToggleAvailability: (vehicle: Vehicle, available: boolean) => void;
}) {
    const { t } = useTranslation();
    return (
        // The container scrolls sideways on a phone instead of widening the page.
        <TableContainer>
            <Table>
                <TableHead>
                    <TableRow>
                        <TableCell>{t('vehicles.fields.displayName')}</TableCell>
                        <TableCell>{t('vehicles.fields.notes')}</TableCell>
                        <TableCell sx={SHRINK}>{t('vehicles.fields.mercedes')}</TableCell>
                        <TableCell align="center" sx={SHRINK}>
                            {t('vehicles.fields.available')}
                        </TableCell>
                    </TableRow>
                </TableHead>
                <TableBody>
                    {rows.map((row) => (
                        <TableRow
                            key={row.id}
                            hover={canEdit}
                            onClick={canEdit ? () => onOpen(row) : undefined}
                            sx={{
                                cursor: canEdit ? 'pointer' : 'default',
                                transition: (theme) =>
                                    theme.transitions.create('background-color', {
                                        duration: theme.transitions.duration.shortest,
                                    }),
                            }}
                        >
                            <TableCell
                                sx={{
                                    color: row.available ? 'text.primary' : 'text.secondary',
                                    fontWeight: 500,
                                    // An availability change fades the display name instead of snapping it.
                                    transition: (theme) =>
                                        theme.transitions.create('color', {
                                            duration: theme.transitions.duration.short,
                                        }),
                                }}
                            >
                                {canEdit ? (
                                    // The keyboard path to the drawer: a real button inside the cell keeps the
                                    // row/cell semantics and the nested switch reachable for screen readers.
                                    <ButtonBase
                                        aria-label={t('vehicles.openDetails', { name: row.displayName })}
                                        onClick={(event) => {
                                            event.stopPropagation();
                                            onOpen(row);
                                        }}
                                        sx={{
                                            font: 'inherit',
                                            fontWeight: 'inherit',
                                            color: 'inherit',
                                            textAlign: 'left',
                                            borderRadius: 1,
                                            px: 0.5,
                                            mx: -0.5,
                                            '&:focus-visible': { outline: '2px solid', outlineColor: 'secondary.main' },
                                        }}
                                    >
                                        {row.displayName}
                                    </ButtonBase>
                                ) : (
                                    row.displayName
                                )}
                            </TableCell>
                            <TableCell>{row.notes ?? '—'}</TableCell>
                            <TableCell>
                                <MercedesChip status={row.mercedesStatus} />
                            </TableCell>
                            <TableCell align="center" onClick={(event) => event.stopPropagation()}>
                                <Switch
                                    checked={row.available}
                                    disabled={pending}
                                    sx={SWITCH_SX}
                                    onChange={(_, checked) => onToggleAvailability(row, checked)}
                                    slotProps={{
                                        input: { 'aria-label': t('vehicles.availableFor', { name: row.displayName }) },
                                    }}
                                />
                            </TableCell>
                        </TableRow>
                    ))}
                </TableBody>
            </Table>
        </TableContainer>
    );
}
